import React, { useEffect, useState } from 'react';
import { Download, Search, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { formatDate } from '@/lib/utils';
import { AdminCreateMembershipModal } from '@/components/admin/AdminCreateModals';

export default function AdminMembers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/reports/members').then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((c) => {
    const s = (c.name + ' ' + c.email + ' ' + c.phone).toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Joined', 'Active Plan', 'Plan Expires', 'Bookings'];
    const lines = filtered.map((c) => [
      c.name, c.email, c.phone, formatDate(c.created_at),
      c.active_membership?.plan_name || '—', c.active_membership ? formatDate(c.active_membership.expires_at) : '—',
      c.bookings_count,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `kba-members-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-members">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Roster</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Members</h1>
          <p className="text-sm text-white/50 mt-2 max-w-xl">Customers with an active paid membership. All registered users live in the "Users" tab.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV}><Download size={14} /> Export CSV</Button>
          <Button onClick={() => setShowCreate(true)} data-testid="new-membership-button"><Plus size={14} /> Add Member</Button>
        </div>
      </div>

      <div className="bg-surface border border-white/10 p-5">
        <Field label="Search">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="name, email, phone…" className="pl-10" data-testid="members-search" />
          </div>
        </Field>
      </div>

      <div className="bg-surface border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="border-b border-white/10 bg-obsidian/40">
            <tr className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              <th className="text-left px-5 py-3">Member</th>
              <th className="text-left px-5 py-3">Contact</th>
              <th className="text-left px-5 py-3">Joined</th>
              <th className="text-left px-5 py-3">Member ID</th>
              <th className="text-left px-5 py-3">Plan</th>
              <th className="text-left px-5 py-3">Reserved Slot</th>
              <th className="text-left px-5 py-3">Bookings</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="px-5 py-12 text-center text-white/40">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="7" className="px-5 py-12 text-center text-white/40">No members.</td></tr>
            ) : filtered.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-5 py-3">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-wider">{c.id.slice(0, 8)}</div>
                </td>
                <td className="px-5 py-3 text-white/70 text-xs">
                  <div>{c.email}</div>
                  <div className="text-white/40">{c.phone}</div>
                </td>
                <td className="px-5 py-3 text-white/60">{formatDate(c.created_at)}</td>
                <td className="px-5 py-3 font-mono text-volt">{c.active_membership?.member_code || '—'}</td>
                <td className="px-5 py-3">
                  {c.active_membership ? (
                    <div>
                      <Badge tone="success">{c.active_membership.plan_name}{c.active_membership.is_team ? ` · Team ${c.active_membership.team_size}` : ''}</Badge>
                      <div className="text-[10px] text-white/40 mt-1">till {formatDate(c.active_membership.expires_at)}</div>
                    </div>
                  ) : <Badge>None</Badge>}
                </td>
                <td className="px-5 py-3 text-white/70 text-xs">
                  {c.active_membership?.reserved_court_name && c.active_membership?.reserved_start_hour != null ? (
                    <>
                      <div>{c.active_membership.reserved_court_name}</div>
                      <div className="text-white/40">{String(c.active_membership.reserved_start_hour).padStart(2,'0')}:00 – {String(c.active_membership.reserved_start_hour + c.active_membership.reserved_duration_hours).padStart(2,'0')}:00 daily</div>
                    </>
                  ) : '—'}
                </td>
                <td className="px-5 py-3 font-mono">{c.bookings_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <AdminCreateMembershipModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}
